import { useLayoutEffect, useState } from "react";
import { SendIcon } from "lucide-react";

import { StickyLayout } from "@/components/organisms/StickyLayout";
import { Mutations } from "@/constants";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { CommandCenterListFooter } from "@/layouts/AppLayout/CommandCenter/CommandCenterListFooter";
import { State } from "@/state";
import { Field, FieldLabel } from "@/components/ui/field";
import { Typography } from "@/components/ui/typography";
import { Spinner } from "@/components/ui/spinner";

import { Input } from "@/components/ui/input";

export const COMMAND_ID = "send-document";

export type SendDocumentContextType = {
  identifier: string;
  /** What the invoice is called: its reference, else its name. */
  title?: string | null;
  /** Who it goes to: its contact, their address, and the company they are. */
  contactIdentifier?: string | null;
  emailAddress?: string | null;
  organizationName?: string | null;
};

/**
 * An invoice emailed to its contact from the business, with a button to the
 * page it is read and paid on - shared, so the link opens. A reply reaches
 * whoever sent it. A contact with no email address is asked for one here: it
 * is saved to the contact, then the invoice is sent there. One with no contact
 * is said to have none, and nothing is sent; the server refuses both too.
 */
function Component({ context }: { context?: SendDocumentContextType }) {
  const clearCommandStack = State((store) => store.clearCommandStack);
  const setSnackbarMessage = State((store) => store.setSnackbarMessage);
  const [typedEmailAddress, setTypedEmailAddress] = useState("");
  const title = context?.title || "This invoice";
  const contactIdentifier = context?.contactIdentifier;
  const emailAddress = context?.emailAddress || typedEmailAddress.trim();

  const { mutate: send, loading: isSending } = useBackendMutation({
    mutation: Mutations.SEND_DOCUMENT,
    callbacks: {
      onSuccess: () => {
        setSnackbarMessage(`Sent ${title} to ${emailAddress}.`);
        clearCommandStack();
      },
    },
  });
  const { mutate: saveEmailAddress, loading: isSaving } = useBackendMutation({
    mutation: Mutations.UPDATE_NODE,
  });

  const isReady =
    Boolean(contactIdentifier) &&
    /^\S+@\S+\.\S+$/.test(emailAddress) &&
    !isSending &&
    !isSaving;
  const submit = () => {
    const identifier = context?.identifier;
    if (!isReady || !identifier) return;
    const sendDocument = () => send({ variables: { input: { identifier } } });
    if (context.emailAddress) {
      sendDocument();
      return;
    }
    saveEmailAddress({
      variables: {
        input: {
          id: btoa(`ContactObject:${contactIdentifier}`),
          node: { contact: { emailAddress } },
        },
      },
      callbacks: { onSuccess: sendDocument },
    });
  };

  return (
    <StickyLayout
      fillHeight={false}
      stickyBottomChildren={
        <CommandCenterListFooter
          buttons={[
            {
              variant: "default",
              size: "sm",
              children: <>{(isSending || isSaving) && <Spinner />}Send</>,
              "data-testid": "send-document-submit",
              hotkey: "enter",
              disabled: isSending || isSaving || !isReady,
              onClick: submit,
            },
          ]}
        />
      }
    >
      <div className="flex flex-col gap-4 p-4">
        <Typography level="body-md" data-testid="send-document-says">
          {!contactIdentifier
            ? `${title} has no contact to send it to: pick the contact it is for.`
            : context?.emailAddress
              ? `${title} is emailed to ${
                  context?.organizationName
                    ? `${context.organizationName} at ${context.emailAddress}`
                    : context.emailAddress
                }, with a link to view and pay it online. Their reply comes to you.`
              : `${title}'s contact has no email address yet. The one you give is saved to the contact, and ${title} is emailed there with a link to view and pay it online.`}
        </Typography>
        {contactIdentifier && !context?.emailAddress && (
          <Field>
            <FieldLabel htmlFor="send-document-email">
              Their email address
            </FieldLabel>
            <Input
              id="send-document-email"
              autoFocus
              type="email"
              value={typedEmailAddress}
              onChange={(event) => setTypedEmailAddress(event.target.value)}
              placeholder="name@company.com"
              data-testid="send-document-email"
            />
          </Field>
        )}
      </div>
    </StickyLayout>
  );
}

export function SendDocumentCommand() {
  const registerCommandDefinition = State(
    (state) => state.registerCommandDefinition,
  );
  const unregisterCommandDefinition = State(
    (state) => state.unregisterCommandDefinition,
  );

  useLayoutEffect(() => {
    registerCommandDefinition({
      id: COMMAND_ID,
      title: "Send by email",
      getLabel: () => "Send by email",
      description: "Email an invoice to its contact",
      component: ({ context }) => (
        <Component context={context as SendDocumentContextType} />
      ),
      ReactIcon: SendIcon,
      isHidden: true,
    });
    return () => unregisterCommandDefinition(COMMAND_ID);
  }, []);

  return null;
}
